// lib/books/file-retention-state.ts
//
// Client-safe pieces of the book file retention policy (services/book-file-retention.ts): the periods and the
// "files were removed" notice the book UIs show instead of broken images. No server imports here.

/** Unpaid previews: files deleted after this many days without owner activity. */
export const UNPAID_INACTIVE_DAYS = 30;
/** Unpaid previews: the owner is emailed once after this many days without activity. */
export const UNPAID_WARN_AFTER_DAYS = 23;
/** A warning must be at least this old before the files are deleted. */
export const UNPAID_WARNING_MIN_AGE_DAYS = 7;
/** Paid books: files deleted this many calendar months after the latest delivery. */
export const PAID_RETENTION_MONTHS = 6;

export type FileRetentionReason = 'unpaid-inactive' | 'paid-expired';

/** books.metadata.retention, as written by the sweep and the "book opened" touch. All fields optional. */
export interface RetentionMeta {
  /** Admin override: never delete this book's files. */
  keep?: boolean;
  /** Last real owner activity: opened the book or used an owner edit route (lib/books/retention-touch.ts). */
  lastOpenedAt?: string;
  /** When the one "your preview will be deleted" email was sent for the current inactivity period. */
  warnedAt?: string;
  /** The date the warning email promised. */
  warnDeleteOn?: string;
  /** Paid books (BOOK_FILE_RETENTION_WARN_PAID): when the one warning before the 6-month deletion was sent. */
  paidWarnedAt?: string;
  paidWarnDeleteOn?: string;
  /** Last time the sweep itself wrote this row (audit only). */
  writtenAt?: string;
  /** Deletion claimed (objects being removed). */
  deletingAt?: string;
  /** Deletion stopped partway: `bucket/path` keys still to remove (finished by the next run). */
  partial?: { remaining: string[]; at: string };
  /** Files deleted; URL fields nulled. */
  deletedAt?: string;
  reason?: FileRetentionReason;
  objectsDeleted?: number;
}

export function readRetention(metadata: unknown): RetentionMeta {
  const r = (metadata as any)?.retention;
  return r && typeof r === 'object' && !Array.isArray(r) ? (r as RetentionMeta) : {};
}

/** The short notice a book UI shows when the retention sweep removed this book's files, else null. */
export function filesRemovedNotice(metadata: unknown): string | null {
  const r = readRetention(metadata);
  if (!r.deletedAt && !r.partial) return null;
  const period = r.reason === 'paid-expired'
    ? `${PAID_RETENTION_MONTHS} months after delivery`
    : `${UNPAID_INACTIVE_DAYS} days without activity`;
  return `This book's files were removed after ${period}. Please contact support@joonoo.art if you need help.`;
}
